/**
 * Pure helpers for the Control Surfaces workbench's nTop mode (FXC-9009):
 * frame-manifest → slider plumbing, kept free of React/network so the snapping
 * and bake-sweep maths are unit-testable (ntopFrames.test.ts).
 *
 * The mode's core gesture is SCRUBBING a pre-baked frame set: the slider must
 * always land ON a baked value (there is no geometry between frames), the
 * slider's range/step must come from the manifest itself, and a bake sweep
 * must expand an input's declared range into a bounded value list. All of
 * that lives here.
 */

import type { NtopFrameSet, NtopInput, NtopNotebook } from "../../api/ntop";

/** Finite frame values of a manifest, ascending. Empty when nothing is baked. */
export function frameValues(frames: NtopFrameSet | null | undefined): number[] {
  return (frames?.frames ?? [])
    .map((f) => f.value)
    .filter((v) => Number.isFinite(v))
    .sort((a, b) => a - b);
}

/**
 * Snap `x` onto the nearest value in `values` (the frames-axis slider snap —
 * geometry only exists AT baked values). Non-finite entries are ignored; ties
 * go to the first nearest; an empty/all-junk list returns `x` unchanged (the
 * caller has nothing to snap to, so it must not invent a value).
 */
export function nearestFrame(values: number[], x: number): number {
  let best = x;
  let bestDist = Infinity;
  for (const v of values) {
    if (!Number.isFinite(v)) continue;
    const d = Math.abs(v - x);
    if (d < bestDist) {
      bestDist = d;
      best = v;
    }
  }
  return best;
}

/**
 * The notebook input the frame set sweeps: the manifest's declared axis when it
 * matches a scalar input, else the FIRST scalar input (the bake default for an
 * unbaked notebook). Null when the notebook declares no scalar inputs at all —
 * there is then nothing to sweep, so the bake action disables itself.
 */
export function framesAxisInput(notebook: NtopNotebook): NtopInput | null {
  const scalars = notebook.inputs.filter((i) => i.type === "scalar");
  if (scalars.length === 0) return null;
  const axisName = notebook.frames?.axis;
  return (axisName ? scalars.find((i) => i.name === axisName) : undefined) ?? scalars[0]!;
}

/** Range + step for the frames-axis slider. */
export interface AxisSliderConfig {
  min: number;
  max: number;
  /** Native-slider step — the smallest gap between baked values, so keyboard
   *  nudges land near a real frame (onChange still snaps via nearestFrame). */
  step: number;
}

/**
 * Slider config from the BAKED values first (the only positions with geometry),
 * falling back to the input's declared range when nothing is baked yet. Null
 * when neither side yields a finite range — the caller renders no slider.
 */
export function axisSliderConfig(
  values: number[],
  input: NtopInput | null | undefined,
): AxisSliderConfig | null {
  const finite = values.filter((v) => Number.isFinite(v)).sort((a, b) => a - b);
  if (finite.length >= 2) {
    let step = Infinity;
    for (let i = 1; i < finite.length; i++) {
      const gap = finite[i]! - finite[i - 1]!;
      if (gap > 0 && gap < step) step = gap;
    }
    return {
      min: finite[0]!,
      max: finite[finite.length - 1]!,
      step: Number.isFinite(step) ? step : 1,
    };
  }
  if (finite.length === 1) return { min: finite[0]!, max: finite[0]!, step: 1 };
  const lo = input?.min;
  const hi = input?.max;
  if (
    typeof lo === "number" && Number.isFinite(lo) &&
    typeof hi === "number" && Number.isFinite(hi) && hi >= lo
  ) {
    const step =
      typeof input?.step === "number" && Number.isFinite(input.step) && input.step > 0
        ? input.step
        : (hi - lo) / 100 || 1;
    return { min: lo, max: hi, step };
  }
  return null;
}

/** Hard cap on a bake sweep — 41 frames is already a few hundred MB of STLs. */
export const BAKE_VALUE_CAP = 41;

/**
 * Expand a scalar input's declared range into the bake sweep values: min..max
 * inclusive by `step`. When the stride would exceed `cap` values, the sweep is
 * REGENERATED as `cap` evenly spaced values across the same range (bounding the
 * bake beats honoring the exact stride). Degenerate declarations fall back
 * honestly: no finite min/max → just the default (or [] when there is none);
 * min === max → that single value; a junk step → an even `cap`-point sweep.
 */
export function bakeAxisValues(input: NtopInput, cap = BAKE_VALUE_CAP): number[] {
  const lo = typeof input.min === "number" && Number.isFinite(input.min) ? input.min : null;
  const hi = typeof input.max === "number" && Number.isFinite(input.max) ? input.max : null;
  if (lo === null || hi === null || hi < lo) {
    return typeof input.default === "number" && Number.isFinite(input.default)
      ? [input.default]
      : [];
  }
  if (hi === lo) return [lo];
  const span = hi - lo;
  const even = (n: number): number[] =>
    Array.from({ length: n }, (_, i) => lo + (i * span) / (n - 1));
  const step =
    typeof input.step === "number" && Number.isFinite(input.step) && input.step > 0
      ? input.step
      : null;
  if (step === null) return even(cap);
  // Inclusive stride count, with a hair of tolerance so 20/5 lands on 5 values.
  const n = Math.floor(span / step + 1e-9) + 1;
  if (n >= cap) return even(cap);
  const out = Array.from({ length: n }, (_, i) => lo + i * step);
  // The declared max matters more than the exact last stride — include it when
  // the stride lands short (e.g. min 0, max 10, step 3 → 0, 3, 6, 9, 10).
  if (out.length === 0 || out[out.length - 1]! < hi - step * 1e-6) out.push(hi);
  return out;
}
